import { uniqueId } from 'lodash-es';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface LoadTrackingSettleArgs {
  /* When the pending count returned to zero. */
  settledAt: number;
  /* Whether any load that resolved during the episode that just ended reported an error. */
  sawError: boolean;
}

export interface LoadTrackingCallbacks {
  /* Invoked every time the pending count returns to zero. Consumers that want "settled for good"
   * rather than "momentarily settled" should debounce, since a subtree swap unregisters the old
   * content's loads and registers the new content's within a single commit.
   */
  onSettle: (args: LoadTrackingSettleArgs) => void;
  /* Invoked every time the pending count rises from zero. */
  onFirstPending?: () => void;
}

interface LoadTrackingState {
  register: (pendingLoadId: string) => void;
  unregister: (pendingLoadId: string, args: { errored: boolean }) => void;
}

const LoadTrackingContext = createContext<LoadTrackingState | null>(null);

interface LoadTrackingProviderProps extends PropsWithChildren, LoadTrackingCallbacks {}

/* Tracks how many data loads are outstanding within its subtree, so an enclosing overlay can tell
 * when its content has finished loading. Registration is imperative and holds no React state:
 * loads come and go on every open, and re-rendering the whole subtree each time one resolved
 * would be both wasteful and visible.
 */
export const LoadTrackingProvider = ({
  children,
  onSettle,
  onFirstPending,
}: LoadTrackingProviderProps) => {
  const [pendingLoadIds] = useState(() => new Set<string>());
  const sawErrorRef = useRef(false);

  // The callbacks are invoked from imperative registration, which must not be re-created (and so
  // re-run every registered load's effect) just because a consumer passed a new closure.
  const onSettleRef = useRef(onSettle);
  onSettleRef.current = onSettle;
  const onFirstPendingRef = useRef(onFirstPending);
  onFirstPendingRef.current = onFirstPending;

  const loadTrackingState: LoadTrackingState = useMemo(
    () => ({
      register: (pendingLoadId) => {
        const wasSettled = pendingLoadIds.size === 0;
        pendingLoadIds.add(pendingLoadId);

        if (wasSettled) {
          sawErrorRef.current = false;
          onFirstPendingRef.current?.();
        }
      },
      unregister: (pendingLoadId, { errored }) => {
        if (!pendingLoadIds.delete(pendingLoadId)) {
          return;
        }

        sawErrorRef.current = sawErrorRef.current || errored;

        if (pendingLoadIds.size === 0) {
          onSettleRef.current({ settledAt: Date.now(), sawError: sawErrorRef.current });
        }
      },
    }),
    [pendingLoadIds],
  );

  return (
    <LoadTrackingContext.Provider value={loadTrackingState}>
      {children}
    </LoadTrackingContext.Provider>
  );
};

/* Reports one outstanding data load to the nearest `LoadTrackingProvider` for as long as
 * `isPending` is true. A no-op when there is no provider above, which is the common case —
 * `useCtwQuery` calls this for every query in the app, and only overlay content sits inside a
 * provider.
 *
 * Call this directly for async work that does not run through `useCtwQuery` but should still hold
 * an overlay open in "loading".
 */
export const useTrackPendingLoad = (isPending: boolean, isError = false) => {
  const loadTracking = useContext(LoadTrackingContext);
  const [pendingLoadId] = useState(() => uniqueId('pending-load-'));

  // Read at unregistration time rather than closed over, because a load that fails flips
  // `isError` in the same render that clears `isPending` — the effect's own closure still holds
  // the pre-failure value.
  const isErrorRef = useRef(isError);
  isErrorRef.current = isError;

  useEffect(() => {
    if (!(loadTracking && isPending)) {
      return undefined;
    }

    loadTracking.register(pendingLoadId);

    return () => loadTracking.unregister(pendingLoadId, { errored: isErrorRef.current });
  }, [loadTracking, isPending, pendingLoadId]);
};
